import React, { useState, useEffect, useMemo } from 'react';
import {
  UserCircle, Bell, Moon, Lock, Info, Save, ChevronDown,
  Check
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { useToast } from '../contexts/ToastContext';
import { useTheme } from '../contexts/ThemeContext';
import { getColorScheme } from '../utils/colorSchemes';
import AppLayout from './AppLayout';
import { useFirebase } from '../contexts/FirebaseContext';
import { authErrorMessage } from '../auth/errors';

const Settings = () => {
  const { userData, updateUserData } = useUser();
  const { user, resetPassword } = useFirebase();
  const [resetPending, setResetPending] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [resetError, setResetError] = useState('');
  const canReset = user?.providerData?.some(provider => provider.providerId === 'password');
  // Add loading and saved message states
const [isLoading, setIsLoading] = useState(false);
const [showSavedMessage, setShowSavedMessage] = useState(false);
const [searchTerm, setSearchTerm] = useState('');
const [selectedCategory, setSelectedCategory] = useState('medicine');
  const { showToast } = useToast();
  const {
  theme,
  setTheme,
  accentColor,
  setAccentColor,
  setBackgroundPattern,
  setFontSize
} = useTheme();
  const [activeSection, setActiveSection] = useState('account');
  const [isInitialized, setIsInitialized] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    emailNotifications: true,
    appNotifications: true,
    clinicalReportAlerts: true,
    menteeActivityAlerts: true,
    darkMode: false,
    fontSize: 'medium',
    accentColor: 'maroon',
    backgroundPattern: 'none',
    dataSharing: true,
    profileVisibility: true,
    menteeVisibility: true,
    expertise: []
  });

  // Set current color scheme based on selected accent color
const colors = useMemo(() => {
  const currentAccentColor = accentColor || formData.accentColor;
  const newColors = getColorScheme(currentAccentColor, theme);
  return newColors;
}, [accentColor, theme, formData.accentColor]);


  // Background patterns with simple, non-repeating background images
  const backgroundPatterns = {
    none: {
      name: 'None',
      style: { background: colors.background },
      previewStyle: { background: '#f9f5f0' }
    },
    dots: {
      name: 'Scattered Dots',
      style: {
        background: colors.background,
        backgroundImage: `
          radial-gradient(circle at 10% 20%, ${colors.primary}30 8px, transparent 8px),
          radial-gradient(circle at 25% 60%, ${colors.primary}30 6px, transparent 6px),
          radial-gradient(circle at 40% 30%, ${colors.primary}30 10px, transparent 10px),
          radial-gradient(circle at 60% 80%, ${colors.primary}30 12px, transparent 12px),
          radial-gradient(circle at 70% 20%, ${colors.primary}30 9px, transparent 9px),
          radial-gradient(circle at 85% 50%, ${colors.primary}30 7px, transparent 7px),
          radial-gradient(circle at 90% 90%, ${colors.primary}30 11px, transparent 11px)
        `,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100% 100%'
      },
      previewStyle: {
        background: '#f9f5f0',
        backgroundImage: `
          radial-gradient(circle at 10% 20%, #8B234250 8px, transparent 8px),
          radial-gradient(circle at 25% 60%, #8B234250 6px, transparent 6px),
          radial-gradient(circle at 40% 30%, #8B234250 10px, transparent 10px),
          radial-gradient(circle at 60% 80%, #8B234250 12px, transparent 12px),
          radial-gradient(circle at 70% 20%, #8B234250 9px, transparent 9px),
          radial-gradient(circle at 85% 50%, #8B234250 7px, transparent 7px),
          radial-gradient(circle at 90% 90%, #8B234250 11px, transparent 11px)
        `,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100% 100%'
      }
    },
    lines: {
      name: 'Abstract Lines',
      style: {
        background: colors.background,
        backgroundImage: `
          linear-gradient(25deg, ${colors.primary}30 0%, ${colors.primary}30 1px, transparent 1px, transparent 100%),
          linear-gradient(-15deg, ${colors.primary}30 0%, ${colors.primary}30 1px, transparent 1px, transparent 100%)
        `,
        backgroundSize: '50px 150px, 70px 200px',
        backgroundPosition: '10% 20%, 85% 75%',
        backgroundRepeat: 'no-repeat'
      },
      previewStyle: {
        background: '#f9f5f0',
        backgroundImage: `
          linear-gradient(25deg, #8B234250 0%, #8B234250 1px, transparent 1px, transparent 100%),
          linear-gradient(-15deg, #8B234250 0%, #8B234250 1px, transparent 1px, transparent 100%)
        `,
        backgroundSize: '50px 150px, 70px 200px',
        backgroundPosition: '10% 20%, 85% 75%',
        backgroundRepeat: 'no-repeat'
      }
    },
    waves: {
      name: 'Gentle Waves',
      style: {
        background: colors.background,
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='600' viewBox='0 0 800 600'%3E%3Cpath fill='none' stroke='%23${colors.primary.slice(1)}' stroke-width='1.5' stroke-opacity='0.3' d='M100,100 Q150,50 200,100 Q250,150 300,100 M500,400 Q550,350 600,400 Q650,450 700,400'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100% 100%'
      },
      previewStyle: {
        background: '#f9f5f0',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='600' viewBox='0 0 800 600'%3E%3Cpath fill='none' stroke='%238B2342' stroke-width='1.5' stroke-opacity='0.4' d='M100,100 Q150,50 200,100 Q250,150 300,100 M500,400 Q550,350 600,400 Q650,450 700,400'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100% 100%'
      }
    },
    herringbone: {
      name: 'Herringbone',
      style: {
        background: colors.background,
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='600' viewBox='0 0 800 600'%3E%3Cpath fill='none' stroke='%23${colors.primary.slice(1)}' stroke-width='1' stroke-opacity='0.3' d='M100,100 L120,120 L100,140 M120,100 L140,120 L120,140 M140,100 L160,120 L140,140 M160,100 L180,120 L160,140 M600,350 L620,370 L600,390 M620,350 L640,370 L620,390 M640,350 L660,370 L640,390 M660,350 L680,370 L660,390'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100% 100%'
      },
      previewStyle: {
        background: '#f9f5f0',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='600' viewBox='0 0 800 600'%3E%3Cpath fill='none' stroke='%238B2342' stroke-width='1' stroke-opacity='0.4' d='M100,100 L120,120 L100,140 M120,100 L140,120 L120,140 M140,100 L160,120 L140,140 M160,100 L180,120 L160,140 M600,350 L620,370 L600,390 M620,350 L640,370 L620,390 M640,350 L660,370 L640,390 M660,350 L680,370 L660,390'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100% 100%'
      }
    },
    geometric: {
      name: 'Geometric',
      style: {
        background: colors.background,
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='600' viewBox='0 0 800 600'%3E%3Crect x='120' y='120' width='50' height='50' fill='%23${colors.primary.slice(1)}' fill-opacity='0.2' transform='rotate(25 145 145)'/%3E%3Cpolygon points='600,150 650,200 550,200' fill='%23${colors.primary.slice(1)}' fill-opacity='0.2'/%3E%3Ccircle cx='200' cy='450' r='40' fill='%23${colors.primary.slice(1)}' fill-opacity='0.2'/%3E%3Crect x='550' y='400' width='60' height='60' fill='%23${colors.primary.slice(1)}' fill-opacity='0.2' transform='rotate(-15 580 430)'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100% 100%'
      },
      previewStyle: {
        background: '#f9f5f0',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='600' viewBox='0 0 800 600'%3E%3Crect x='120' y='120' width='50' height='50' fill='%238B2342' fill-opacity='0.3' transform='rotate(25 145 145)'/%3E%3Cpolygon points='600,150 650,200 550,200' fill='%238B2342' fill-opacity='0.3'/%3E%3Ccircle cx='200' cy='450' r='40' fill='%238B2342' fill-opacity='0.3'/%3E%3Crect x='550' y='400' width='60' height='60' fill='%238B2342' fill-opacity='0.3' transform='rotate(-15 580 430)'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100% 100%'
      }
    },
    chevron: {
      name: 'Chevron',
      style: {
        background: colors.background,
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='600' viewBox='0 0 800 600'%3E%3Cpath fill='%23${colors.primary.slice(1)}' fill-opacity='0.2' d='M150,200 L200,150 L250,200 L300,150 L350,200 M450,400 L500,350 L550,400 L600,350 L650,400'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100% 100%'
      },
      previewStyle: {
        background: '#f9f5f0',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='600' viewBox='0 0 800 600'%3E%3Cpath fill='%238B2342' fill-opacity='0.3' d='M150,200 L200,150 L250,200 L300,150 L350,200 M450,400 L500,350 L550,400 L600,350 L650,400'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100% 100%'
      }
 }
  };

  // Add this entire object here:
  const interestOptions = {
    medicine: [
      "Anesthesiology", "Cardiology", "Dermatology", "Emergency Medicine",
      "Family Medicine", "Gastroenterology", "Internal Medicine",
      "Neurology", "Obstetrics & Gynecology", "Oncology", "Ophthalmology",
      "Orthopedic Surgery", "Otolaryngology", "Pediatrics", "Psychiatry",
      "Radiology", "Urology", "Nephrology", "Endocrinology",
      "Pulmonology", "Rheumatology", "Infectious Disease", "Pathology"
    ],
    dentistry: [
      "General Dentistry", "Endodontics", "Oral Surgery", "Implantology",
      "Periodontics", "Prosthodontics", "Orthodontics", "Pediatric Dentistry",
      "Dental Public Health", "Oral Pathology", "Maxillofacial Surgery"
    ],
    pharmacy: [
      "Clinical Pharmacy", "Compounding", "Hospital Pharmacy",
      "Pharmaceutical Research", "Retail Pharmacy", "Pharmacology",
      "Pharmacognosy", "Pharmacoeconomics", "Regulatory Affairs"
    ],
    nursing: [
      "Critical Care", "Emergency", "Geriatric", "Mental Health",
      "Midwifery", "Oncology", "Pediatric", "Public Health",
      "Surgical", "Nurse Practitioner", "Nursing Education"
    ],
    student: [
      "Pre-Med", "Pre-Dental", "Pre-Pharmacy", "Pre-Nursing",
      "Research", "Clinical Volunteering", "MCAT/DAT Prep",
      "Application Process", "Interview Preparation"
    ],
    other: [
      "Physical Therapy", "Occupational Therapy", "Speech Therapy",
      "Healthcare Administration", "Medical Technology", "Radiography",
      "Public Health", "Nutrition", "Healthcare Informatics"
    ]
  };

  // Get user's field from registration
  const availableOptions = interestOptions[selectedCategory] || interestOptions.medicine;

  // Initialize form data from userData when it loads

useEffect(() => {
  if (userData && !isInitialized) {
    setFormData(prev => ({
      ...prev,
      name: userData.name || '',
      email: userData.email || '',
      emailNotifications: userData.emailNotifications ?? false,
      appNotifications: userData.appNotifications ?? false,
      clinicalReportAlerts: userData.clinicalReportAlerts ?? false,
      menteeActivityAlerts: userData.menteeActivityAlerts ?? false,
      dataSharing: userData.dataSharing ?? false,
      profileVisibility: userData.profileVisibility ?? false,
      menteeVisibility: userData.menteeVisibility ?? false,
      darkMode: theme === 'dark',
      accentColor: userData.accentColor || 'maroon',
      backgroundPattern: userData.backgroundPattern || 'none',
      fontSize: userData.fontSize || 'medium',
      expertise: userData.expertise || [],
    }));

    // ONLY set theme values on initial load
    if (userData.accentColor) {
      setAccentColor(userData.accentColor);
    }
    if (userData.backgroundPattern) {
      setBackgroundPattern(userData.backgroundPattern);
    }
    if (userData.fontSize) {
      setFontSize(userData.fontSize);
    }

    setIsInitialized(true); // Prevent future overrides
  }
}, [userData, theme, isInitialized, setAccentColor, setBackgroundPattern, setFontSize]);

  // Update colors when accent color or background pattern changes
  useEffect(() => {
    // This will force a re-render with the new colors when accent color changes
  }, [formData.accentColor, formData.backgroundPattern]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'darkMode') {
      setTheme(checked ? 'dark' : 'light');
    }

    // Create the updated form data
    const updatedFormData = {
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    };

    setFormData(updatedFormData);
  };

const handleAccentColorChange = (colorValue) => {

  // Update both formData and ThemeContext immediately
  setFormData(prev => ({ ...prev, accentColor: colorValue }));
  setAccentColor(colorValue);

};

const handleBackgroundPatternChange = (patternKey) => {

  // Update both formData and ThemeContext immediately
  setFormData(prev => ({ ...prev, backgroundPattern: patternKey }));
  setBackgroundPattern(patternKey);

};

  const handleExpertiseChange = (expertise, isChecked) => {
    setFormData(prev => ({
      ...prev,
      expertise: isChecked
        ? [...prev.expertise, expertise]
        : prev.expertise.filter(item => item !== expertise)
    }));
  };

const handleSaveSettings = async (e) => {    e.preventDefault();

    if (!['account', 'appearance'].includes(activeSection) || isLoading || user?.isAnonymous) return;

    // Clear an earlier success indicator before attempting another save.
    setShowSavedMessage(false);
    setIsLoading(true);

    try {
      if (userData) {
        let dataToUpdate = {};

        switch (activeSection) {
          case 'account':
            dataToUpdate = {
              name: formData.name,
              expertise: formData.expertise
            };
            break;
          case 'appearance':
            dataToUpdate = {
              darkMode: formData.darkMode,
              fontSize: formData.fontSize,
              accentColor: formData.accentColor,
              backgroundPattern: formData.backgroundPattern
            };
            break;
          default:
            break;
        }

        await updateUserData(dataToUpdate);
        showToast(`${activeSection.charAt(0).toUpperCase() + activeSection.slice(1)} settings saved!`, "success");

        // Success loading states
        setIsLoading(false);
        setShowSavedMessage(true);
        setTimeout(() => setShowSavedMessage(false), 3000);


      }
} catch (error) {
      showToast(`Error saving settings: ${error.message}`, "error");
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!canReset || resetPending || !user.email) return;
    setResetPending(true);
    setResetMessage('');
    setResetError('');
    try {
      await resetPassword(user.email);
      setResetMessage('Password reset requested. Check your inbox and spam folder for instructions.');
    } catch (error) {
      setResetError(authErrorMessage(error));
    } finally { setResetPending(false); }
  };

return (
  <AppLayout activeTab="settings">
    <div
style={{
        ...(backgroundPatterns[formData.backgroundPattern] || backgroundPatterns.none).style
      }}
      className="rounded-lg shadow-md"
    >
<div className="rounded-lg shadow-sm p-6 mb-4" style={{ backgroundColor: colors.cardBg + '35' }}>
  <nav className="text-sm mb-6">
    <ol className="list-none p-0 inline-flex">
      <li className="flex items-center">
        <button onClick={() => navigate('/app')} className="text-gray-500 hover:text-gray-700">Home</button>
        <span className="mx-2">/</span>
      </li>
      <li className="text-gray-700 font-semibold">Settings</li>
    </ol>
  </nav>

  <h2 className="text-3xl font-serif" style={{ color: colors.primary }}>Settings</h2>
  <p className="text-gray-600 mt-1">Manage your account preferences and profile settings</p>

  <div className="flex flex-col md:flex-row gap-8 mt-6">
          <div className="md:w-1/4">
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => setActiveSection('account')}
                  className={`w-full text-left p-3 rounded-lg flex items-center transition-colors duration-300 ${
                    activeSection === 'account'
                      ? `text-white`
                      : `text-gray-700 hover:bg-opacity-30`
                  }`}
                  style={{
  backgroundColor: activeSection === 'account' ? colors.primary : 'rgba(255, 255, 255, 0.2)'
}}
                >
                  <UserCircle className="mr-2" /> Account
                </button>
              </li>
              <li>
                <button
                  onClick={() => setActiveSection('notifications')}
                  className={`w-full text-left p-3 rounded-lg flex items-center transition-colors duration-300 ${
                    activeSection === 'notifications'
                      ? `text-white`
                      : `text-gray-700 hover:bg-opacity-30`
                  }`}
                  style={{
                    backgroundColor: activeSection === 'notifications' ? colors.primary : 'rgba(255, 255, 255, 0.3)'
                  }}
                >
                  <Bell className="mr-2" /> Notifications
                </button>
              </li>
              <li>
                <button
                  onClick={() => setActiveSection('appearance')}
                  className={`w-full text-left p-3 rounded-lg flex items-center transition-colors duration-300 ${
                    activeSection === 'appearance'
                      ? `text-white`
                      : `text-gray-700 hover:bg-opacity-30`
                  }`}
                  style={{
                    backgroundColor: activeSection === 'appearance' ? colors.primary : 'rgba(255, 255, 255, 0.3)'
                  }}
                >
                  <Moon className="mr-2" /> Appearance
                </button>
              </li>
              <li>
                <button
                  onClick={() => setActiveSection('privacy')}
                  className={`w-full text-left p-3 rounded-lg flex items-center transition-colors duration-300 ${
                    activeSection === 'privacy'
                      ? `text-white`
                      : `text-gray-700 hover:bg-opacity-30`
                  }`}
                  style={{
                    backgroundColor: activeSection === 'privacy' ? colors.primary : 'rgba(255, 255, 255, 0.3)'
                  }}
                >
                  <Lock className="mr-2" /> Privacy
                </button>
              </li>
              <li>
                <button
                  onClick={() => setActiveSection('about')}
                  className={`w-full text-left p-3 rounded-lg flex items-center transition-colors duration-300 ${
                    activeSection === 'about'
                      ? `text-white`
                      : `text-gray-700 hover:bg-opacity-30`
                  }`}
                  style={{
                    backgroundColor: activeSection === 'about' ? colors.primary : 'rgba(255, 255, 255, 0.3)'
                  }}
                >
                  <Info className="mr-2" /> About
                </button>
              </li>
            </ul>
          </div>

          {/* Settings Content */}
          <div className="md:w-3/4">
      <div className="bg-white bg-opacity-95 rounded-lg p-6 shadow-sm">
    <form onSubmit={handleSaveSettings}>
              {/* Account Settings */}
              {activeSection === 'account' && (
                <div>
                  <h3 className="text-xl mb-4 font-serif" style={{ color: colors.primary }}>Account Settings</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block mb-1 font-medium">Name</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full p-2 border border-gray-300 rounded text-gray-800 bg-white"
                      />
                    </div>
                    <div>
                      <label className="block mb-1 font-medium">Email</label>
                      <input
                        type="email"
                        name="email"
                        readOnly
                        title="Sign-in email (read-only)"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full p-2 border border-gray-300 rounded text-gray-800 bg-white"
                      />
                    </div>
                    <div>
                      <label className="block mb-1 font-medium">Role</label>
                      <div className="relative">
                        <select
                          name="role"
                          value={userData?.role || 'mentor'}
                          className="appearance-none w-full p-2 bg-white border border-gray-300 rounded text-gray-800 pr-8"
                          disabled
                        >
                          <option value="student">Student</option>
                          <option value="mentor">Mentor</option>
                          <option value="admin">Administrator</option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                          <ChevronDown className="h-4 w-4" />
                        </div>
                      </div>
                    </div>

                    <div className="mt-6">
                      <label className="block mb-3 font-medium">Areas of Interest</label>

                      {/* Category Tabs */}
                      <div className="mb-4">
                        <div className="flex flex-wrap gap-2">
                          {Object.keys(interestOptions).map(category => (
                            <button
                              key={category}
                              onClick={() => setSelectedCategory(category)}
                              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                selectedCategory === category
                                  ? 'text-white'
                                  : 'text-gray-600 bg-gray-200 hover:bg-gray-300'
                              }`}
                              style={{
                                backgroundColor: selectedCategory === category ? colors.primary : undefined
                              }}
                            >
                              {category.charAt(0).toUpperCase() + category.slice(1)}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Search/Filter Bar */}
                      <div className="mb-4">
                        <input
                          type="text"
                          placeholder="Search interests..."
                          className="w-full p-4 border border-gray-300 rounded-lg text-gray-800 bg-white"
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>

                      {/* Selected Interests (Tags) */}
                      {formData.expertise.length > 0 && (
                        <div className="mb-4">
                          <p className="text-sm text-gray-600 mb-2">Selected ({formData.expertise.length}):</p>
                          <div className="flex flex-wrap gap-2">
                            {formData.expertise.map(interest => (
                              <span
                                key={interest}
                                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium text-white"
                                style={{ backgroundColor: colors.primary }}
                              >
                                {interest}
                                <button
                                  onClick={() => handleExpertiseChange(interest, false)}
                                  className="ml-2 text-white hover:text-gray-200"
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Options Grid with Categories */}
                      <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-4 bg-gray-50">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {availableOptions
                            .filter(option => !searchTerm || option.toLowerCase().includes(searchTerm.toLowerCase()))
                            .map(interest => (
                              <label
                                key={interest}
                                className={`flex items-center p-3 rounded-lg cursor-pointer transition-all ${
                                  formData.expertise.includes(interest)
                                    ? 'bg-white border-2 shadow-md'
                                    : 'bg-white border border-gray-200 hover:border-gray-400'
                                }`}
                                style={{
                                  borderColor: formData.expertise.includes(interest) ? colors.primary : undefined
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={formData.expertise.includes(interest)}
                                  onChange={(e) => handleExpertiseChange(interest, e.target.checked)}
                                  className="mr-2"
                                  style={{ accentColor: colors.primary }}
                                />
                                <span className="text-sm">{interest}</span>
                              </label>
                            ))}
                        </div>
                      </div>

                      {/* Quick Stats */}
                      <p className="text-xs text-gray-500 mt-2">
                        {availableOptions.length} options available • {formData.expertise.length} selected
                      </p>
                    </div>

                    <h4 className="text-lg mt-6 mb-2 font-serif" style={{ color: colors.primary }}>Password and sign-in</h4>
                    {canReset ? <div>
                      <p className="text-sm text-gray-600 mb-3">Use an email link to reset the password for your Firebase account.</p>
                      <button type="button" disabled={resetPending} onClick={handleResetPassword} className="px-4 py-2 rounded bg-[#76263c] text-white disabled:opacity-60">{resetPending ? 'Requesting reset...' : 'Send password reset email'}</button>
                      {resetMessage && <p role="status" className="mt-3">{resetMessage}</p>}
                      {resetError && <p role="alert" className="mt-3 text-red-700">{resetError}</p>}
                    </div> : <p className="text-sm text-gray-600">{user?.isAnonymous ? 'You are using a guest session. Sign in to manage an account.' : 'This account uses a sign-in provider such as Google. Manage your password with that provider; this screen does not set a separate password.'}</p>}

                  </div>
                </div>
              )}

              {/* Notification Settings */}
              {activeSection === 'notifications' && (
                <div>
                  <h3 className="text-xl mb-4 font-serif" style={{ color: colors.primary }}>Notification Settings</h3>
                  <p className="mb-4">Notifications are not available yet. Saved preferences are shown below but do not send alerts.</p>
                  <div className="space-y-4">
                    <div className="flex items-center bg-white p-3 rounded border border-gray-200">
                      <input
                        type="checkbox"
                        disabled id="emailNotifications"
                        name="emailNotifications"
                        checked={formData.emailNotifications}
                        onChange={handleInputChange}
                        className="mr-2"
                      />
                      <label htmlFor="emailNotifications" className="flex flex-col">
                        <span className="font-medium">Email Notifications</span>
                        <span className="text-sm text-gray-500">Receive email updates about your mentorship activities</span>
                      </label>
                    </div>
                    <div className="flex items-center bg-white p-3 rounded border border-gray-200">
                      <input
                        type="checkbox"
                        disabled id="appNotifications"
                        name="appNotifications"
                        checked={formData.appNotifications}
                        onChange={handleInputChange}
                        className="mr-2"
                      />
                      <label htmlFor="appNotifications" className="flex flex-col">
                        <span className="font-medium">App Notifications</span>
                        <span className="text-sm text-gray-500">Receive in-app notifications and alerts</span>
                      </label>
                    </div>
                    <div className="flex items-center bg-white p-3 rounded border border-gray-200">
                      <input
                        type="checkbox"
                        disabled id="clinicalReportAlerts"
                        name="clinicalReportAlerts"
                        checked={formData.clinicalReportAlerts}
                        onChange={handleInputChange}
                        className="mr-2"
                      />
                      <label htmlFor="clinicalReportAlerts" className="flex flex-col">
                        <span className="font-medium">Clinical Report Alerts</span>
                        <span className="text-sm text-gray-500">Get notified when mentees submit clinical reports</span>
                      </label>
                    </div>
                    <div className="flex items-center bg-white p-3 rounded border border-gray-200">
                      <input
                        type="checkbox"
                        disabled id="menteeActivityAlerts"
                        name="menteeActivityAlerts"
                        checked={formData.menteeActivityAlerts}
                        onChange={handleInputChange}
                        className="mr-2"
                      />
                      <label htmlFor="menteeActivityAlerts" className="flex flex-col">
                        <span className="font-medium">Mentee Activity Alerts</span>
                        <span className="text-sm text-gray-500">Get notified about your mentees' progress</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Appearance Settings */}
              {activeSection === 'appearance' && (
                <div>
                  <h3 className="text-xl mb-4 font-serif" style={{ color: colors.primary }}>Appearance Settings</h3>
                  <div className="space-y-4">
                    <div className="flex items-center bg-white p-3 rounded border border-gray-200">
                      <input
                        type="checkbox"
                        id="darkMode"
                        name="darkMode"
                        checked={formData.darkMode}
                        onChange={handleInputChange}
                        className="mr-2"
                      />
                      <label htmlFor="darkMode" className="flex flex-col">
                        <span className="font-medium">Dark Mode</span>
                        <span className="text-sm text-gray-500">Use dark theme for the application</span>
                      </label>
                    </div>
                    <div>
                      <label className="block mb-1 font-medium">Font Size</label>
                      <div className="relative">
                        <select
                          name="fontSize"
                          value={formData.fontSize}
                          onChange={handleInputChange}
                          className="appearance-none w-full p-2 bg-white border border-gray-300 rounded text-gray-800 pr-8"
                        >
                          <option value="small">Small</option>
                          <option value="medium">Medium</option>
                          <option value="large">Large</option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                          <ChevronDown className="h-4 w-4" />
                        </div>
                      </div>
                    </div>
                    <div>
  <label className="block mb-3 font-medium">Accent Color</label>
  <div className="grid grid-cols-4 gap-2">
    {[
      { value: 'maroon', label: 'Maroon', color: '#8B2342' },
      { value: 'teal', label: 'Teal', color: '#057A8A' },
      { value: 'indigo', label: 'Indigo', color: '#4338CA' },
      { value: 'emerald', label: 'Emerald', color: '#047857' }
    ].map(colorOption => {
const isSelected = (accentColor || formData.accentColor) === colorOption.value;
      return (
        <button
          key={colorOption.value}
          type="button"
          onClick={(e) => {
            e.preventDefault();
            handleAccentColorChange(colorOption.value);
          }}
          className={`flex flex-col items-center cursor-pointer p-3 rounded border transition-all ${
            isSelected
              ? 'border-gray-800 shadow-md'
              : 'border-gray-200 hover:border-gray-400'
          }`}
        >
          <div
            className="w-8 h-8 rounded-full mb-2"
            style={{ backgroundColor: colorOption.color }}
          ></div>
          <span className="text-sm">{colorOption.label}</span>
        </button>
      );
    })}
  </div>
</div>
                    <div className="mt-6">
                      <label className="block mb-3 font-medium">Background Pattern</label>
                      <div className="grid grid-cols-3 gap-3">
                        {Object.entries(backgroundPatterns).map(([key, pattern]) => (
                          <div
                            key={key}
                            onClick={() => handleBackgroundPatternChange(key)}
                            className={`flex flex-col items-center cursor-pointer p-3 rounded border transition-all ${
                              formData.backgroundPattern === key
                                ? 'border-gray-800 shadow-md'
                                : 'border-gray-200 hover:border-gray-400'
                            }`}
                          >
                            <div
                              className="w-full h-16 rounded mb-2"
                              style={pattern.previewStyle}
                            ></div>
                            <span className="text-sm">{pattern.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Privacy Settings */}
              {activeSection === 'privacy' && (
                <div>
                  <h3 className="text-xl mb-4 font-serif" style={{ color: colors.primary }}>Privacy Settings</h3>
                  <p className="mb-4">Sharing controls are not available yet. These saved preferences do not publish your profile or mentee list. Private profiles remain protected.</p>
                  <div className="space-y-4">
                    <div className="flex items-center bg-white p-3 rounded border border-gray-200">
                      <input
                        type="checkbox"
                        disabled id="dataSharing"
                        name="dataSharing"
                        checked={formData.dataSharing}
                        onChange={handleInputChange}
                        className="mr-2"
                      />
                      <label htmlFor="dataSharing" className="flex flex-col">
                        <span className="font-medium">Allow Data Sharing for Improvement</span>
                        <span className="text-sm text-gray-500">Share anonymous usage data to improve platform experience</span>
                      </label>
                    </div>
                    <div className="flex items-center bg-white p-3 rounded border border-gray-200">
                      <input
                        type="checkbox"
                        disabled id="profileVisibility"
                        name="profileVisibility"
                        checked={formData.profileVisibility}
                        onChange={handleInputChange}
                        className="mr-2"
                      />
                      <label htmlFor="profileVisibility" className="flex flex-col">
                        <span className="font-medium">Profile Visibility</span>
                        <span className="text-sm text-gray-500">Make your profile publicly visible on the platform</span>
                      </label>
                    </div>
                    <div className="flex items-center bg-white p-3 rounded border border-gray-200">
                      <input
                        type="checkbox"
                        disabled id="menteeVisibility"
                        name="menteeVisibility"
                        checked={formData.menteeVisibility}
                        onChange={handleInputChange}
                        className="mr-2"
                      />
                      <label htmlFor="menteeVisibility" className="flex flex-col">
                        <span className="font-medium">Mentee List Visibility</span>
                        <span className="text-sm text-gray-500">Allow other mentors to see your mentee list</span>
                      </label>
                    </div>
                    <div className="mt-4">
                      <button
                        type="button"
                        className="text-white px-4 py-2 rounded flex items-center transition-colors duration-300"
                        disabled aria-label="Download My Data unavailable" title="Data export is not available yet"
                        style={{ backgroundColor: colors.accent }}
                      >
                        Download My Data — unavailable
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* About */}
              {activeSection === 'about' && (
                <div>
                  <h3 className="text-xl mb-4 font-serif" style={{ color: colors.primary }}>About Pathway2Practice</h3>
                  <div className="bg-white p-4 rounded border border-gray-200 mb-4">
                    <p className="mb-2"><strong>Status:</strong> In development</p>
                    <p>Shared mentoring features are not connected yet.</p>
                  </div>
                  <p className="mb-4">Pathway2Practice is a medical mentorship platform designed to connect experienced practitioners with students and residents for career development guidance and clinical learning opportunities.</p>
                  <div className="space-y-2 mt-6">
                    <button
                      type="button" disabled
                      className="w-full bg-white hover:bg-gray-50 text-gray-800 px-4 py-2 rounded flex items-center justify-between transition-colors duration-300 border border-gray-200"
                    >
                      <span>Terms of Service — unavailable</span>
                      <ChevronDown className="h-4 w-4" />
                    </button>
                    <button
                      type="button" disabled
                      className="w-full bg-white hover:bg-gray-50 text-gray-800 px-4 py-2 rounded flex items-center justify-between transition-colors duration-300 border border-gray-200"
                    >
                      <span>Privacy Policy — unavailable</span>
                      <ChevronDown className="h-4 w-4" />
                    </button>
                    <button
                      type="button" disabled
                      className="w-full bg-white hover:bg-gray-50 text-gray-800 px-4 py-2 rounded flex items-center justify-between transition-colors duration-300 border border-gray-200"
                    >
                      <span>Contact Support — unavailable</span>
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="mt-6 text-sm text-center text-gray-600">© {new Date().getFullYear()} Pathway2Practice. All rights reserved.</p>
                </div>
              )}

              {['account', 'appearance'].includes(activeSection) && (
  <div className="mt-6">
    <div className="flex items-center gap-3">
      <button
        type="submit"
        disabled={isLoading || user?.isAnonymous}
        className={`text-white px-6 py-2 rounded-full flex items-center transition-all duration-300 ${
          isLoading ? 'opacity-75 cursor-not-allowed' : 'hover:shadow-lg'
        }`}
        style={{ backgroundColor: colors.primary }}
      >
        {isLoading && (
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
        )}
        {!isLoading && <Save className="mr-2 h-4 w-4" />}
        <span>{isLoading ? 'Saving...' : 'Save Changes'}</span>
      </button>

      {showSavedMessage && (
        <div className="flex items-center gap-2 text-green-600 font-medium transition-all duration-300">
          <Check className="w-5 h-5" />
          Saved!
        </div>
      )}
    </div>
  </div>
)}
            </form>
          </div>
          </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default function SettingsScreen() {
  const { user } = useFirebase();
  return user ? <Settings key={user.uid} /> : null;
}
